// src/components/billing/PolicyHistoryCard.tsx
import { Button, Group, Paper, Stack, Text } from "@mantine/core";
import type { FeePolicy } from "../../apis/tenantFeePolicy";
import { formatDate, formatMoney, formatPercent } from "../../utils/format";
import { FeePolicyStatusBadge } from "./FeePolicyStatusBadge";

interface Props {
  version: FeePolicy;
  /** Previous active version, if any — when present, render Compare button. */
  previous?: FeePolicy | null;
  onView: (v: FeePolicy) => void;
  onCompare: (a: FeePolicy, b: FeePolicy) => void;
}

function methodSummary(p: FeePolicy): string {
  if (p.calculationMethod === "PERCENTAGE" && p.feeRate)
    return `PERCENTAGE ${formatPercent(Number(p.feeRate) * 100)}`;
  if (p.calculationMethod === "FLAT" && p.flatFeeAmount)
    return `FLAT ${formatMoney(p.flatFeeAmount, p.feeCurrency)}`;
  return p.calculationMethod;
}

function dateRange(p: FeePolicy): string {
  const from = p.effectiveFrom ? formatDate(p.effectiveFrom) : "—";
  if (p.status === "SCHEDULED") return `${from}-нд идэвхжинэ`;
  if (p.status === "ACTIVE") return `${from} → одоо`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const until = (p as any).effectiveUntil ? formatDate((p as any).effectiveUntil) : "—";
  return `${from} → ${until}`;
}

export function PolicyHistoryCard({ version, previous, onView, onCompare }: Props) {
  const summary =
    `${methodSummary(version)} · ${version.refundPolicy}` +
    ` · платформ шимтгэл хасах: ${version.absorbPlatformFee ? "тийм" : "үгүй"}`;

  return (
    <Paper p="md" withBorder radius="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="xs">
            <FeePolicyStatusBadge status={version.status} />
            <Text size="sm" fw={600}>v{version.currentVersion}</Text>
            <Text size="sm" c="dimmed">{dateRange(version)}</Text>
          </Group>
          <Text size="sm" c="dimmed">{summary}</Text>
        </Stack>
        <Group gap="xs">
          <Button variant="subtle" size="xs" onClick={() => onView(version)}>
            Харах
          </Button>
          {previous && (
            <Button variant="subtle" size="xs" onClick={() => onCompare(previous, version)}>
              v{previous.currentVersion}-тэй харьцуулах
            </Button>
          )}
        </Group>
      </Group>
    </Paper>
  );
}
